import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Moon, Sun, Bookmark, LogIn, LogOut, Menu, X, LayoutDashboard } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import NotificationBell from './NotificationBell';
import Logo from './Logo';

function Header() {
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setMobileMenuOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header 
      className="sticky top-0 z-50 glass-effect bg-white/70 dark:bg-black/60 backdrop-blur-xl border-b border-black/10 dark:border-white/10"
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <Logo size={36} />
          </Link>

          {/* Desktop Search */}
          <form onSubmit={handleSearch} className="hidden md:flex items-center flex-1 max-w-md mx-8">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Nachrichten durchsuchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-full border-gray-300 dark:border-gray-700"
                data-testid="search-input"
              />
            </div>
          </form>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-3">
            {user?.is_admin && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/admin')}
                className="rounded-full"
                title="Admin Dashboard"
                data-testid="admin-dashboard-button"
              >
                <LayoutDashboard className="h-5 w-5" />
              </Button>
            )}
            {user && <NotificationBell />}
            {user && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/bookmarks')}
                className="rounded-full"
                data-testid="bookmarks-button"
              >
                <Bookmark className="h-5 w-5" />
              </Button>
            )}
            
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full"
              data-testid="theme-toggle"
            >
              {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            </Button>

            {user ? (
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-700 dark:text-gray-300">{user.name}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  className="rounded-full"
                  data-testid="logout-button"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Abmelden
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => navigate('/login')}
                className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white"
                data-testid="login-button"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Anmelden
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden rounded-full"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-black/10 dark:border-white/10" data-testid="mobile-menu">
            <form onSubmit={handleSearch} className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Suchen..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 rounded-full"
                  data-testid="mobile-search-input"
                />
              </div>
            </form>
            <div className="space-y-2">
              {user ? (
                <>
                  {user.is_admin && (
                    <Button
                      variant="ghost"
                      className="w-full justify-start rounded-full"
                      onClick={() => {
                        navigate('/admin');
                        setMobileMenuOpen(false);
                      }}
                      data-testid="mobile-admin-button"
                    >
                      <LayoutDashboard className="h-4 w-4 mr-2" />
                      Admin Dashboard
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    className="w-full justify-start rounded-full"
                    onClick={() => {
                      navigate('/bookmarks');
                      setMobileMenuOpen(false);
                    }}
                    data-testid="mobile-bookmarks-button"
                  >
                    <Bookmark className="h-4 w-4 mr-2" />
                    Lesezeichen
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start rounded-full"
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    data-testid="mobile-logout-button"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Abmelden
                  </Button>
                </>
              ) : (
                <Button
                  className="w-full rounded-full bg-[#0055FF] hover:bg-[#0044CC]"
                  onClick={() => {
                    navigate('/login');
                    setMobileMenuOpen(false);
                  }}
                  data-testid="mobile-login-button"
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  Anmelden
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

export default Header;
